/**
 * Sniffing de magic numbers no upload.
 *
 * O upload é presigned: a API assina um `content-type`, mas quem escolhe os
 * BYTES é o cliente. A allowlist de MIME (`isUploadAllowed`) corre ANTES do
 * upload e acredita no que o cliente declara — um `.exe` renomeado `foto.png` e
 * anunciado `image/png` atravessa-a. Esta função lê a assinatura real do início
 * do ficheiro e recusa a mentira.
 *
 * Decide só sobre tipos com assinatura fiável. SVG, JSON, CSV e texto não têm
 * número mágico — para esses devolve `true` e a defesa fica na allowlist de MIME
 * (e, no SVG, em nunca ser servido inline). Puro de propósito: vive no `shared`
 * para ser testável sem infra e para o cliente poder, se quiser, pré-validar.
 */
/** Compara `header` com uma sequência de bytes num dado offset. */
function matchesAt(header, offset, bytes) {
    if (header.length < offset + bytes.length)
        return false;
    for (let i = 0; i < bytes.length; i++) {
        if (header[offset + i] !== bytes[i])
            return false;
    }
    return true;
}
/** Os bytes ASCII de uma string curta — as assinaturas de texto ("RIFF", "%PDF"). */
const ascii = (text) => Array.from(text, (c) => c.charCodeAt(0));
/**
 * Um matcher por content-type. `true` = a assinatura bate certo com o tipo
 * declarado. Contentores ISO-BMFF (mp4/mov/avif) partilham o átomo `ftyp` no
 * offset 4; distinguir mp4 de mov não é uma questão de segurança (ambos são
 * media real, não executáveis), por isso basta exigir o `ftyp` e, no avif,
 * confirmar o brand de imagem para não o deixar passar por vídeo à toa.
 */
const SIGNATURES = {
    "image/png": (h) => matchesAt(h, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
    "image/jpeg": (h) => matchesAt(h, 0, [0xff, 0xd8, 0xff]),
    "image/gif": (h) => matchesAt(h, 0, ascii("GIF87a")) || matchesAt(h, 0, ascii("GIF89a")),
    "image/webp": (h) => matchesAt(h, 0, ascii("RIFF")) && matchesAt(h, 8, ascii("WEBP")),
    "image/avif": (h) => matchesAt(h, 4, ascii("ftyp")) &&
        (matchesAt(h, 8, ascii("avif")) || matchesAt(h, 8, ascii("avis")) || matchesAt(h, 8, ascii("mif1"))),
    "video/mp4": (h) => matchesAt(h, 4, ascii("ftyp")),
    "video/quicktime": (h) => matchesAt(h, 4, ascii("ftyp")) ||
        matchesAt(h, 4, ascii("moov")) ||
        matchesAt(h, 4, ascii("mdat")) ||
        matchesAt(h, 4, ascii("free")) ||
        matchesAt(h, 4, ascii("wide")),
    // Matroska/WebM partilham o mesmo cabeçalho EBML — vídeo e áudio.
    "video/webm": (h) => matchesAt(h, 0, [0x1a, 0x45, 0xdf, 0xa3]),
    "audio/webm": (h) => matchesAt(h, 0, [0x1a, 0x45, 0xdf, 0xa3]),
    "audio/ogg": (h) => matchesAt(h, 0, ascii("OggS")),
    "audio/wav": (h) => matchesAt(h, 0, ascii("RIFF")) && matchesAt(h, 8, ascii("WAVE")),
    // MP3: ou uma tag ID3 à cabeça, ou o frame-sync de 11 bits a 1 (0xFFEx).
    "audio/mpeg": (h) => matchesAt(h, 0, ascii("ID3")) || (h.length >= 2 && h[0] === 0xff && (h[1] & 0xe0) === 0xe0),
    "application/pdf": (h) => matchesAt(h, 0, ascii("%PDF")),
    // ZIP: local file header, ou o marcador de arquivo vazio/spanned.
    "application/zip": (h) => matchesAt(h, 0, [0x50, 0x4b, 0x03, 0x04]) ||
        matchesAt(h, 0, [0x50, 0x4b, 0x05, 0x06]) ||
        matchesAt(h, 0, [0x50, 0x4b, 0x07, 0x08]),
};
/** Tipos cujo número mágico sabemos verificar — o resto passa por design. */
export const SNIFFABLE_TYPES = new Set(Object.keys(SIGNATURES));
/**
 * `true` se o conteúdo condiz com o tipo declarado, OU se o tipo não tem
 * assinatura fiável para verificar. `false` só quando há assinatura conhecida e
 * os bytes NÃO batem certo — ou seja, quando alguém mentiu sobre o que enviou.
 *
 * `header` bastam ~16 bytes (o offset mais fundo que lemos é o 12), mas aceita
 * qualquer tamanho; um buffer curto de mais para uma dada assinatura falha o
 * `matchesAt` e é tratado como não-correspondência.
 */
export function sniffUpload(contentType, header) {
    const mime = contentType.split(";")[0]?.trim().toLowerCase() ?? "";
    const check = SIGNATURES[mime];
    if (!check)
        return true;
    return check(header);
}
//# sourceMappingURL=magic.js.map